// apps/accounts/src/pages/materials/Categories.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, X, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const Categories = () => {
  const { role } = useAuth();
  const canManage = ['admin', 'accounts'].includes(role);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', parent: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = async () => {
    try {
      const records = await pb.collection('material_categories').getFullList({
        sort: 'name',
        expand: 'parent',
        requestKey: null,
      });
      setCategories(records);
    } catch (err) {
      if (err.isAbort) return;
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await pb.collection('material_categories').delete(id);
      toast.success('Category deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', description: '', parent: '' });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setFormData({ name: cat.name, description: cat.description || '', parent: cat.parent || '' });
    setFormError('');
    setShowForm(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = { name: formData.name.trim(), description: formData.description.trim(), parent: formData.parent || null };
      if (editing) {
        await pb.collection('material_categories').update(editing.id, data);
        toast.success('Category updated');
      } else {
        await pb.collection('material_categories').create(data);
        toast.success('Category created');
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" /></div>;

  const filtered = categories.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Material Categories</h1>
          <p className="text-sm text-slate-500">Manage material categories</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Category
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search categories..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button variant="outline" onClick={() => setSearch('')}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Parent</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Description</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-500">No categories</td></tr> : filtered.map(c => (
                <tr key={c.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">{c.expand?.parent?.name || '—'}</td>
                  <td className="px-4 py-3">{c.description || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      {canManage && (
                        <>
                          <button onClick={() => openEdit(c)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(c.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Edit Category' : 'New Category'}</h2>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <Input name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parent Category</label>
                <select name="parent" value={formData.parent} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400">
                  <option value="">None</option>
                  {categories.filter(c => c.id !== editing?.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Input name="description" value={formData.description} onChange={handleChange} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editing ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;